import { ONBOARDING_STATUS } from '@realadvisor/constants';
import { z } from 'zod';

/**
 * The trigger-written history of one user's self-onboarding
 * (hasura-crm/migrations/crm/1791901637516_log_onboarding_milestones):
 * `onboarding_enrolled` when the wizard is (re)assigned, then one
 * `onboarding_status_changed` per `users.onboarding_status` write, with the
 * old and new status in `extra_data`, and one `agent_profile_visibility_changed`
 * per `users.show_in_agency_pages` write. Accounts approved by the backfill
 * have none of these rows, so every date below is optional.
 */
export type OnboardingHistoryActivity = {
  activity_type: string;
  created_at: string;
  created_by?: string | null;
  extra_data?: unknown;
  creator?: { full_name?: string | null } | null;
};

export type OnboardingStepKey =
  | 'started'
  | 'submitted'
  | 'approved'
  | 'published';
export type OnboardingStepState = 'done' | 'skipped' | 'current' | 'upcoming';

export type OnboardingStep = {
  key: OnboardingStepKey;
  state: OnboardingStepState;
  at: string | null;
  by: string | null;
};

const StatusChangeSchema = z.object({
  from: z.string().nullable().optional(),
  to: z.string().nullable().optional(),
});

const VisibilityChangeSchema = z.object({
  to: z.boolean().nullable().optional(),
});

const isPublication = (activity: OnboardingHistoryActivity) =>
  activity.activity_type === 'agent_profile_visibility_changed' &&
  VisibilityChangeSchema.safeParse(activity.extra_data).data?.to === true;

type StatusChange = {
  from: string | null;
  to: string | null;
  activity: OnboardingHistoryActivity;
};

const toStatusChange = (
  activity: OnboardingHistoryActivity,
): StatusChange | null => {
  if (activity.activity_type !== 'onboarding_status_changed') {
    return null;
  }
  const parsed = StatusChangeSchema.safeParse(activity.extra_data);
  if (!parsed.success) {
    return null;
  }
  return {
    from: parsed.data.from ?? null,
    to: parsed.data.to ?? null,
    activity,
  };
};

const stamp = (activity: OnboardingHistoryActivity | undefined) => ({
  at: activity?.created_at ?? null,
  by: activity?.creator?.full_name ?? null,
});

const findLast = <T>(items: T[], predicate: (item: T) => boolean) =>
  [...items].reverse().find(predicate);

/**
 * Started → Submitted → Approved → Published for the user's current
 * onboarding round.
 *
 * Only the rows since the latest enrolment count: an agent re-enrolled into a
 * new agency starts over, and their first round must not date the second.
 *
 * `in_progress → approved` (or `null → approved`, for an agent enrolled before
 * the status existed) is a skip only when someone other than the agent made
 * it. The agent makes the same jump themselves when they submit the wizard on
 * a claimed public profile, which is approved on submission.
 *
 * Published is the round's first publication: once it happened, the step stays
 * done even if the profile was unpublished since, so the card never asks to
 * publish a profile someone took down on purpose. Approved accounts with no
 * round at all (the backfill) get no Published step.
 */
export const getOnboardingSteps = ({
  userId,
  status,
  published,
  activities,
}: {
  userId: string;
  status: string | null | undefined;
  published: boolean;
  activities: OnboardingHistoryActivity[];
}): OnboardingStep[] => {
  const sorted = [...activities].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );
  const lastEnrolment = findLast(
    sorted,
    activity => activity.activity_type === 'onboarding_enrolled',
  );
  const round =
    lastEnrolment == null
      ? sorted
      : sorted.filter(
          activity => activity.created_at >= lastEnrolment.created_at,
        );
  const changes = round
    .map(toStatusChange)
    .filter((change): change is StatusChange => change != null);

  const opened = findLast(
    changes,
    change => change.to === ONBOARDING_STATUS.inProgress && change.from == null,
  );
  const started: OnboardingStep = {
    key: 'started',
    state: 'done',
    ...stamp(lastEnrolment ?? opened?.activity),
  };

  const pendingPublication: OnboardingStep = {
    key: 'published',
    state: 'upcoming',
    at: null,
    by: null,
  };

  if (status === ONBOARDING_STATUS.inProgress) {
    return [
      started,
      { key: 'submitted', state: 'current', at: null, by: null },
      { key: 'approved', state: 'upcoming', at: null, by: null },
      pendingPublication,
    ];
  }

  const submission = findLast(
    changes,
    change => change.to === ONBOARDING_STATUS.pendingReview,
  );

  if (status === ONBOARDING_STATUS.pendingReview) {
    return [
      started,
      { key: 'submitted', state: 'done', ...stamp(submission?.activity) },
      { key: 'approved', state: 'current', at: null, by: null },
      pendingPublication,
    ];
  }

  const approval = findLast(
    changes,
    change => change.to === ONBOARDING_STATUS.approved,
  );
  const approvedBy = approval?.activity.created_by ?? null;
  const skipped =
    approval != null &&
    (approval.from === ONBOARDING_STATUS.inProgress || approval.from == null) &&
    approvedBy != null &&
    approvedBy !== userId;

  const steps: OnboardingStep[] = [
    started,
    skipped
      ? { key: 'submitted', state: 'skipped', ...stamp(approval.activity) }
      : {
          key: 'submitted',
          state: 'done',
          // A claimed profile is submitted and approved in the same write.
          ...stamp(submission?.activity ?? approval?.activity),
        },
    { key: 'approved', state: 'done', ...stamp(approval?.activity) },
  ];

  // No round at all (the backfill): no Published step, live profile or not.
  if (lastEnrolment == null && approval == null) {
    return steps;
  }
  const publication = round.find(isPublication);
  if (publication != null || published) {
    // Published before the visibility log existed: done, without a date.
    return [
      ...steps,
      { key: 'published', state: 'done', ...stamp(publication) },
    ];
  }
  return [...steps, { key: 'published', state: 'current', at: null, by: null }];
};
